"use client";

import {
  type BlocksContent,
  BlocksRenderer as StrapiBlocksRenderer,
} from "@strapi/blocks-react-renderer";
import { MarkdownContent } from "@/components/markdown/markdown-content";
import {
  extractTextFromChildren,
  renderMarkdownTable,
} from "@/components/markdown/markdown-table";
import { parseMarkdownTable } from "@/components/markdown/utils";
import { TipCallout } from "@/components/tip-callout";
import {
  extractInlineText,
  getMarkdownContentFromBlock,
  isRecord,
} from "@/lib/article-toc/extract-from-blocks";
import { isExternalLink } from "@/lib/external-link";
import { cn } from "@/lib/utils";
import { Img } from "./image";

const TIP_BLOCK_MARKER = "[[FOODANGO_TIP]]";
const TIP_BLOCK_PLACEHOLDER = "Tutaj wpisz tresc porady.";

const HEADING_TAGS = {
  1: "h1",
  2: "h2",
  3: "h3",
  4: "h4",
  5: "h5",
  6: "h6",
} as const;

const createHeadingRenderer =
  (anchorId?: string) =>
  ({
    children,
    level,
  }: {
    readonly children?: React.ReactNode;
    readonly level: 1 | 2 | 3 | 4 | 5 | 6;
  }) => {
    const Tag = HEADING_TAGS[level];

    return (
      <Tag className={anchorId ? "scroll-mt-28" : undefined} id={anchorId}>
        {children}
      </Tag>
    );
  };

const getTipContentFromBlock = (block: BlocksContent[number]): string | null => {
  if (
    !isRecord(block) ||
    (block.type !== "quote" && block.type !== "paragraph") ||
    !Array.isArray(block.children)
  ) {
    return null;
  }

  const textContent = extractInlineText(block.children).trim();

  if (!textContent.startsWith(TIP_BLOCK_MARKER)) {
    return null;
  }

  const tipContent = textContent.slice(TIP_BLOCK_MARKER.length).trim();

  return tipContent.length > 0 ? tipContent : TIP_BLOCK_PLACEHOLDER;
};

export const BlockRenderer = ({
  content,
  dangerouslySetInnerHTML,
  roundedImages = false,
  anchors,
  markdownAnchors,
  anchorKeyPrefix = "",
  anchorStartIndex = 0,
}: {
  readonly content?: BlocksContent;
  dangerouslySetInnerHTML?: boolean;
  roundedImages?: boolean;
  anchors?: Record<string, string>;
  markdownAnchors?: Record<string, Record<number, string>>;
  anchorKeyPrefix?: string;
  anchorStartIndex?: number;
}) => {
  if (!content) return null;

  const blockRenderers = {
    image: ({
      image,
    }: {
      readonly image: {
        readonly alternativeText?: string | null;
        readonly height: number;
        readonly url: string;
        readonly width: number;
        readonly [key: string]: unknown;
      };
    }) => {
      return (
        <div className="grid w-full place-items-center">
          <Img
            alt={typeof image.alternativeText === "string" ? image.alternativeText : ""}
            className={cn({
              "rounded-xl": roundedImages,
            })}
            height={typeof image.height === "number" ? image.height : undefined}
            src={String(image.url)}
            width={typeof image.width === "number" ? image.width : undefined}
          />
        </div>
      );
    },
    paragraph: ({ children }: { readonly children?: React.ReactNode }) => {
      if (dangerouslySetInnerHTML) {
        // This is a workaround to render HTML content in a paragraph block
        const dangerouslyParagraph = children as {
          props: { text: string };
        }[];
        const text = dangerouslyParagraph?.[0]?.props.text as string;
        return <p dangerouslySetInnerHTML={{ __html: text }} />;
      }

      // Try to extract text and check if it's a markdown table
      const text = extractTextFromChildren(children);
      if (text) {
        const tableRows = parseMarkdownTable(text);
        if (tableRows) {
          return renderMarkdownTable(tableRows);
        }
      }

      return <p>{children}</p>;
    },
    quote: ({ children }: { readonly children?: React.ReactNode }) => {
      return <blockquote>{children}</blockquote>;
    },
    link: ({
      children,
      url,
    }: {
      readonly children?: React.ReactNode;
      readonly url: string;
    }) => {
      const external = isExternalLink(url);

      return (
        <a href={url} rel={external ? "nofollow" : undefined}>
          {children}
        </a>
      );
    },
  };

  return (
    <>
      {content.map((block, index) => {
        const anchorKey = `${anchorKeyPrefix}b:${anchorStartIndex + index}`;
        const markdownContent = getMarkdownContentFromBlock(block);

        if (markdownContent) {
          return (
            <MarkdownContent
              content={markdownContent}
              headingIds={markdownAnchors?.[anchorKey]}
              key={`md-${index}`}
            />
          );
        }

        const tipContent = getTipContentFromBlock(block);

        if (tipContent) {
          return <TipCallout content={tipContent} key={`tip-${index}`} />;
        }

        return (
          <StrapiBlocksRenderer
            blocks={{
              ...blockRenderers,
              heading: createHeadingRenderer(anchors?.[anchorKey]),
            }}
            content={[block] as BlocksContent}
            key={`block-${index}`}
          />
        );
      })}
    </>
  );
};
